import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Loader2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { disputeService } from "@/services";
import type { DisputeDto, DisputeStatus } from "@shared/types/DisputeDto";

const STATUS_CONFIG: Record<DisputeStatus, { label: string; variant: "default" | "success" | "warning" | "destructive" | "secondary" | "info" }> = {
  OPEN: { label: "Open", variant: "destructive" },
  UNDER_REVIEW: { label: "Under Review", variant: "warning" },
  AWAITING_CLIENT_RESPONSE: { label: "Awaiting Response", variant: "info" },
  IN_MEDIATION: { label: "In Mediation", variant: "warning" },
  RESOLVED: { label: "Resolved", variant: "success" },
  CLOSED: { label: "Closed", variant: "secondary" },
};

function DisputeRow({ dispute }: { dispute: DisputeDto }) {
  const navigate = useNavigate();
  const config = STATUS_CONFIG[dispute.status] ?? { label: dispute.status, variant: "secondary" as const };

  const handleClick = () => {
    navigate("/disputes", { state: { selectedDisputeId: dispute.id } });
  };

  return (
    <button
      onClick={handleClick}
      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{dispute.title}</p>
          <p className="text-xs text-muted-foreground capitalize">{dispute.category.toLowerCase().replace("_", " ")}</p>
        </div>
      </div>
      <Badge variant={config.variant} className="shrink-0 text-[10px]">
        {config.label}
      </Badge>
    </button>
  );
}

export function DisputesCard() {
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState<DisputeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRecent = useCallback(async () => {
    try {
      const all = await disputeService.getDisputes();
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      const recent = all
        .filter((d) => new Date(d.createdAt) >= cutoff)
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      setDisputes(recent);
    } catch {
      // non-critical
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-base font-semibold">Recent Disputes</CardTitle>
        <Button variant="ghost" size="sm" onClick={() => navigate("/disputes")} className="text-xs">
          View all <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : disputes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
              <AlertTriangle className="h-6 w-6 text-emerald-400" />
            </div>
            <p className="text-sm text-muted-foreground">No recent disputes</p>
          </div>
        ) : (
          <div className="space-y-1">
            {disputes.slice(0, 5).map((dispute) => (
              <DisputeRow key={dispute.id} dispute={dispute} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
